import { useState, useEffect } from 'react';
import { CheckCircle, Circle, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STEPS = [
  { id: 'link_gmail', title: 'Link a Gmail account', desc: 'Connect at least one Gmail to start sending', path: '/accounts' },
  { id: 'import_leads', title: 'Import your first leads', desc: 'Upload a CSV or TXT file with contacts', path: '/leads' },
  { id: 'create_campaign', title: 'Create a campaign', desc: 'Set up your email sequence and launch', path: '/campaigns/new' },
];

export default function Onboarding() {
  const [completed, setCompleted] = useState({});
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const d = localStorage.getItem('bulkify_onboarding_dismissed');
    if (d) { setDismissed(true); return; }

    Promise.all([
      api.get('/api/accounts').then(r => r.data.accounts?.length > 0),
      api.get('/api/leads/lists').then(r => r.data.lists?.length > 0),
      api.get('/api/campaigns').then(r => r.data.campaigns?.length > 0),
    ]).then(([hasAccounts, hasLeads, hasCampaigns]) => {
      setCompleted({ link_gmail: hasAccounts, import_leads: hasLeads, create_campaign: hasCampaigns });
      if (hasAccounts && hasLeads && hasCampaigns) {
        localStorage.setItem('bulkify_onboarding_dismissed', '1');
        setDismissed(true);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem('bulkify_onboarding_dismissed', '1');
    setDismissed(true);
  };

  if (dismissed) return null;
  const doneCount = Object.values(completed).filter(Boolean).length;
  if (doneCount === STEPS.length) return null;

  return (
    <div style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(99,102,241,0.05))', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', marginBottom: 24, position: 'relative' }}>
      <button onClick={dismiss} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer' }}>
        <X size={15} />
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>🚀 Get started with Bulkify</div>
        <span style={{ fontSize: 11, background: 'var(--blue-dim)', color: '#93c5fd', padding: '2px 8px', borderRadius: 20 }}>{doneCount}/{STEPS.length} done</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {STEPS.map(step => (
          <div key={step.id} onClick={() => !completed[step.id] && navigate(step.path)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: completed[step.id] ? 'rgba(34,197,94,0.06)' : 'var(--bg2)', border: `1px solid ${completed[step.id] ? 'rgba(34,197,94,0.2)' : 'var(--border)'}`, cursor: completed[step.id] ? 'default' : 'pointer', transition: 'all 0.15s' }}>
            {completed[step.id]
              ? <CheckCircle size={18} color="var(--green)" style={{ flexShrink: 0 }} />
              : <Circle size={18} color="var(--text3)" style={{ flexShrink: 0 }} />
            }
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: completed[step.id] ? 'var(--text2)' : 'var(--text)', textDecoration: completed[step.id] ? 'line-through' : 'none' }}>{step.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{step.desc}</div>
            </div>
            {!completed[step.id] && <ChevronRight size={14} color="var(--text3)" />}
          </div>
        ))}
      </div>
    </div>
  );
}
